import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { assignmentsApi } from "@/api";
import type { CreateAssignmentData, UpdateAssignmentData } from "@/api";
import { QUERY_KEYS } from "@/constants";

/** Darsning topshirig'i. 404 — "hali yo'q", xato emas. */
export const useAssignment = (lessonId: number | undefined) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_ASSIGNMENT, lessonId],
    queryFn: () => assignmentsApi.getByLesson(lessonId as number),
    enabled: !!lessonId,
    retry: false,
    select: res => res.data,
  });

const useInvalidateAssignment = (lessonId: number, courseId?: number) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_ASSIGNMENT, lessonId] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_COURSE_READINESS, courseId] });
  };
};

export const useCreateAssignment = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAssignment(lessonId, courseId);
  return useMutation({
    mutationFn: (data: CreateAssignmentData) => assignmentsApi.create(data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};

export const useUpdateAssignment = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAssignment(lessonId, courseId);
  return useMutation({
    mutationFn: (vars: { id: number; data: UpdateAssignmentData }) =>
      assignmentsApi.update(vars.id, vars.data),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.UPDATED"));
    },
  });
};

export const useAddAssignmentAttachments = (lessonId: number, courseId?: number) => {
  const { t } = useTranslation();
  const invalidate = useInvalidateAssignment(lessonId, courseId);
  return useMutation({
    mutationFn: (vars: { id: number; files: File[]; onProgress?: (percent: number) => void }) =>
      assignmentsApi.addAttachments(vars.id, vars.files, vars.onProgress),
    onSuccess: () => {
      invalidate();
      toast.success(t("COMMON.ADDED"));
    },
  });
};
